import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { tossPaymentsService } from '@/services/tossPayments';
import { cartService } from '@/services/cart';
import { orderService } from '@/services/order';
import type { CheckoutState } from './checkout.types';

interface UsePaymentWidgetProps {
  checkoutData: CheckoutState | null;
  orderId: string | null;
  validationErrors: string[];
  userId: string | undefined;
  showToast: (message: string) => void;
}

export function usePaymentWidget({ 
  checkoutData, 
  orderId, 
  validationErrors, 
  userId,
  showToast
}: UsePaymentWidgetProps) {
  const navigate = useNavigate();
  const [isWidgetReady, setIsWidgetReady] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  
  const widgetInitialized = useRef(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  // 위젯 재초기화 함수 (지연 추가)
  const resetWidget = useCallback(async () => {
    console.log('🔄 위젯 재초기화 시작...');
    setIsResetting(true);
    widgetInitialized.current = false;
    setIsWidgetReady(false);
    tossPaymentsService.cleanup();
    
    // TossPayments SDK 완전 정리를 위한 지연
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setIsResetting(false);
    console.log('✅ 위젯 재초기화 완료 - SDK 정리 대기 완료');
  }, []);

  // 결제 실패 복구 함수
  const handlePaymentError = useCallback(async (error: any) => {
    console.log('🔧 결제 오류 복구 시작...');
    setIsRecovering(true);
    
    try {
      // 1. 위젯 정리 및 재초기화 준비 (비동기 대기)
      await resetWidget();
      
      // 2. 장바구니 상태 복원
      const cartId = await cartService.getCurrentCart(userId || '').then(cart => cart?.id || '');
      if (cartId) {
        await tossPaymentsService.handlePaymentFailure(cartId, checkoutData?.orderType);
        console.log('✅ 장바구니 상태 복원 완료');
      }
      
      // 3. 사용자 피드백
      showToast(error.message || '결제 처리 중 오류가 발생했습니다. 다시 시도해주세요.');
      
      // 4. 2초 후 복구 완료 (위젯은 이미 준비됨)
      setTimeout(() => {
        setIsRecovering(false);
        console.log('✅ 결제 환경 복구 완료 - 재시도 가능');
      }, 2000);
      
    } catch (recoveryError) {
      console.error('복구 실패:', recoveryError);
      showToast('결제 환경 복구에 실패했습니다. 장바구니로 돌아갑니다.');
      setTimeout(() => {
        navigate('/cart');
      }, 2000);
    }
  }, [resetWidget, userId, checkoutData?.orderType, showToast, navigate]);

  // TossPayments 위젯 초기화
  useEffect(() => {
    const initializeWidget = async () => {
      if (!checkoutData || !orderId || validationErrors.length > 0 || widgetInitialized.current || isResetting) {
        return;
      }

      // 0원(포인트 전액) 결제는 위젯을 렌더링하지 않고 바로 준비 완료 처리
      if (checkoutData.finalPrice <= 0) {
        setIsWidgetReady(true);
        widgetInitialized.current = true;
        return;
      }

      try {
        console.log('🎯 TossPayments 위젯 초기화 시작', {
          orderId,
          finalPrice: checkoutData.finalPrice,
          clientKey: import.meta.env.VITE_TOSSPAYMENTS_CLIENT_KEY?.substring(0, 20) + '...'
        });

        await tossPaymentsService.initialize();
        console.log('✅ TossPayments SDK 초기화 완료');

        // 결제 위젯 렌더링 (setAmount 포함)
        await tossPaymentsService.renderPaymentWidget(
          'payment-widget-container',
          checkoutData.finalPrice,
          {
            variantKey: 'widgetEco'
          }
        );
        console.log('✅ 결제 위젯 렌더링 완료');

        widgetInitialized.current = true;
        setIsWidgetReady(true);
        console.log('✅ 위젯 준비 완료 - 사용자가 결제 가능');
      } catch (error) {
        console.error('❌ Failed to initialize payment widget:', error);
        showToast('결제 위젯 초기화에 실패했습니다');
      }
    };

    initializeWidget();
  }, [checkoutData, orderId, validationErrors, isResetting, showToast]);

  // 페이지 이탈 시 PENDING 주문 정리
  useEffect(() => {
    // cleanup 함수 설정
    cleanupRef.current = () => {
      if (orderId && !isProcessingPayment) {
        console.log('Cleaning up pending order on page unmount:', orderId);
        // 비동기로 취소 처리 (페이지 이탈 시에도 실행됨)
        orderService.cancelPendingOrder(orderId, '결제 페이지 이탈').catch((error: any) => {
          console.error('Failed to cancel pending order on unmount:', error);
        });
      }
    };

    // cleanup 함수 반환
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, [orderId, isProcessingPayment]);

  // 브라우저 뒤로가기/새로고침 감지
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (orderId && !isProcessingPayment) {
        // 주문이 있고 결제 진행 중이 아니면 경고
        e.preventDefault();
        e.returnValue = '결제를 진행 중입니다. 페이지를 떠나시겠습니까?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [orderId, isProcessingPayment]);

  return {
    isWidgetReady,
    isRecovering,
    isResetting,
    isProcessingPayment,
    setIsProcessingPayment,
    handlePaymentError
  };
}
